import React, { useState, useEffect } from 'react';
import Modal from './Modal';

// Generate time options every 30 min from 06:00 to 21:00
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 21; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh  = h % 12 || 12;
      const mm  = m === 0 ? '00' : '30';
      const ampm = h < 12 ? 'AM' : 'PM';
      opts.push({ label: `${hh}:${mm} ${ampm}`, value: h * 60 + m });
    }
  }
  return opts;
})();

const toLabel = (mins) => {
  const opt = TIME_OPTIONS.find(o => o.value === mins);
  return opt ? opt.label : '';
};

const today = () => new Date().toISOString().split('T')[0];
const DEFAULT_FROM = 8 * 60;   // 8:00 AM
const DEFAULT_TO   = 9 * 60;   // 9:00 AM

export default function BookingForm({ isOpen, onClose, onSubmit, school, loading }) {
  const [form,     setForm]    = useState({ childName: '', childAge: '', date: today(), notes: '' });
  const [fromMins, setFromMins] = useState(DEFAULT_FROM);
  const [toMins,   setToMins]   = useState(DEFAULT_TO);
  const [error,    setError]   = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm({ childName: '', childAge: '', date: today(), notes: '' });
      setFromMins(DEFAULT_FROM);
      setToMins(DEFAULT_TO);
      setError('');
    }
  }, [isOpen]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFromChange = (e) => {
    const val = Number(e.target.value);
    setFromMins(val);
    // Auto-advance To to at least From + 30 min, capped at From + 9 hours
    if (toMins <= val) setToMins(Math.min(val + 30, val + 9 * 60));
  };

  const handleToChange = (e) => {
    setToMins(Number(e.target.value));
  };

  // To options: must be > fromMins and within 9 hours of fromMins
  const toOptions = TIME_OPTIONS.filter(
    (o) => o.value > fromMins && o.value <= fromMins + 9 * 60
  );

  const submit = (e) => {
    e.preventDefault();
    if (!form.childName || !form.childAge || !form.date) { setError('Child name, age, and date are required'); return; }
    if (form.date < today()) { setError('Booking date must be today or in the future'); return; }
    if (toMins <= fromMins) { setError('End time must be after start time'); return; }
    setError('');
    const timeSlot     = `${toLabel(fromMins)} – ${toLabel(toMins)}`;
    const pricePerHour = school?.pricePerHour || 0;
    const pricePerDay  = school?.pricePerDay  || 0;
    const hrs          = (toMins - fromMins) / 60;
    const totalPayable = pricePerHour > 0
      ? Math.round(pricePerHour * hrs)
      : pricePerDay > 0 ? pricePerDay : 0;
    onSubmit({ ...form, timeSlot, pricePerHour, pricePerDay, totalPayable });
  };

  const hours        = (toMins - fromMins) / 60;
  const pricePerHour = school?.pricePerHour || 0;
  const pricePerDay  = school?.pricePerDay  || 0;
  const totalPayable = pricePerHour > 0
    ? Math.round(pricePerHour * hours)
    : pricePerDay > 0 ? pricePerDay : 0;
  const priceLabel   = pricePerHour > 0
    ? `${hours}hr${hours !== 1 ? 's' : ''}`
    : pricePerDay > 0 ? '/ day' : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`📅 Book Slot at ${school?.name || 'School'}`}
      footer={
        <div className="booking-footer">
          {totalPayable > 0 ? (
            <div className="booking-total-pill">
              <span className="booking-total-label">Total</span>
              <span className="booking-total-amount">₹{totalPayable}</span>
              <span className="booking-total-hrs">{priceLabel}</span>
            </div>
          ) : <div />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={onClose} disabled={loading}>Cancel</button>
            <button className="btn btn-primary" onClick={submit} disabled={loading}>
              {loading ? 'Submitting…' : 'Confirm Booking'}
            </button>
          </div>
        </div>
      }
    >
      {school && (
        <div style={{ padding: '10px 14px', background: 'var(--primary-light)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--primary-dark)' }}>
          🏫 <strong>{school.name}</strong>
          {school.operatingHours && <> &nbsp;·&nbsp; {school.operatingHours}</>}
          {school.ageRange      && <> &nbsp;·&nbsp; Age: {school.ageRange}</>}
          {school.pricePerHour > 0 && <> &nbsp;·&nbsp; 💰 ₹{school.pricePerHour}/hr</>}
        </div>
      )}

      {error && <div className="form-error mb-16">{error}</div>}

      <form onSubmit={submit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Child's Name *</label>
            <input className="form-input" value={form.childName} onChange={set('childName')} placeholder="Alex Smith" required />
          </div>
          <div className="form-group">
            <label className="form-label">Child's Age *</label>
            <input className="form-input" type="number" min="0" max="12" value={form.childAge} onChange={set('childAge')} placeholder="e.g. 3" required />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Preferred Date *</label>
          <input className="form-input" type="date" value={form.date} onChange={set('date')} min={today()} required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start From *</label>
            <select className="form-select" value={fromMins} onChange={handleFromChange}>
              {TIME_OPTIONS.filter(o => o.value < 21 * 60).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">To *</label>
            <select className="form-select" value={toMins} onChange={handleToChange}>
              {toOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Additional Notes</label>
          <textarea className="form-textarea" value={form.notes} onChange={set('notes')} placeholder="Any special requirements or notes for the school…" />
        </div>
      </form>
    </Modal>
  );
}
